import type { Appointment, TimeOff } from '../api/client'
import { SHORT_MONTHS, minutesToTime } from '../catalog/format'
import { weekdayIndex } from './dates'

const SHORT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// shortDay is "Wed 4 Nov" for a calendar date.
export function shortDay(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-').map(Number)
  return `${SHORT_DAYS[weekdayIndex(iso)]} ${d} ${SHORT_MONTHS[m - 1]}`
}

// timeOffProblems checks a new stretch of time off before it is added
// (review 2026-10-14): it must start today or later, end on or after its
// first day, and not be a range she already has.
export function timeOffProblems(
  draft: { starts_on: string; ends_on: string },
  existing: TimeOff[],
  today: string,
): Record<string, string> {
  const starts = draft.starts_on
  const ends = draft.ends_on || draft.starts_on
  if (!starts) return { starts_on: 'Choose the first day.' }
  if (starts < today) return { starts_on: 'Choose dates from today on.' }
  if (ends < starts) return { ends_on: 'Choose a last day on or after the first.' }
  if (existing.some((o) => o.starts_on <= starts && o.ends_on >= ends)) {
    return { starts_on: 'That range is already in your time off.' }
  }
  return {}
}

// upcomingTimeOff is what the list shows: time off that hasn't ended yet.
export function upcomingTimeOff(list: TimeOff[], today: string): TimeOff[] {
  return list.filter((o) => o.ends_on >= today)
}

// appointmentsInTimeOff are her booked appointments inside the new stretch.
export function appointmentsInTimeOff(appointments: Appointment[], starts: string, ends: string): Appointment[] {
  return appointments
    .filter((a) => a.status === 'booked' && a.date >= starts && a.date <= ends)
    .sort((a, b) => (a.date === b.date ? a.start_minute - b.start_minute : a.date < b.date ? -1 : 1))
}

// timeOffWarning: "You have 1 appointment in these days: Priya, Wed 4 Nov
// 10:00. Add time off anyway?" — the first three named, then how many more.
export function timeOffWarning(appointments: Appointment[]): string {
  if (appointments.length === 0) return ''
  const named = appointments
    .slice(0, 3)
    .map((a) => `${a.client?.first_name ?? 'No client'}, ${shortDay(a.date)} ${minutesToTime(a.start_minute)}`)
  const rest = appointments.length - named.length
  const count = appointments.length === 1 ? '1 appointment' : `${appointments.length} appointments`
  return `You have ${count} in these days: ${named.join('; ')}${rest > 0 ? `; and ${rest} more` : ''}. Add time off anyway?`
}
